import axios from 'axios'

export interface EmailDomain {
  id: number
  domain: string
  status: 'pending' | 'verified' | 'failed'
  spf_verified: boolean
  dkim_verified: boolean
  dmarc_verified: boolean
  return_path_verified: boolean
  is_ready_for_sending: boolean
  created_at: string
}

export interface DnsRecord {
  hostname: string
  type: 'TXT' | 'CNAME'
  value: string
  valid?: boolean
}

export interface DnsRecordsResponse {
  domain: string
  status: string
  records: {
    spf: DnsRecord | null
    dkim: DnsRecord[] | null
    dmarc: DnsRecord | null
    tracking: DnsRecord | null
    return_path: DnsRecord | null
  }
}

export type DnsDiagnosticState = 'missing' | 'mismatch' | 'propagating' | 'ok' | 'unknown'

export type DnsDiagnosticSummary =
  | 'nothing_created'
  | 'incomplete'
  | 'mismatch'
  | 'propagating'
  | 'ready'
  | 'unknown'
  | 'domain_not_found'

export type DnsZoneLookup = 'found' | 'not_found' | 'lookup_failed'

export type DnsDiagnosticHint =
  | 'multiple_spf'
  | 'spf_missing_include'
  | 'wrong_target'
  | 'wrong_type'
  | 'dmarc_missing_rua'

export interface DnsProviderInfo {
  key: string
  name: string
  manager_url: string
  docs: Record<string, string>
}

export interface DnsProviderContext {
  domain: string
  zone: string | null
  zone_lookup: DnsZoneLookup
  nameservers: string[]
  provider: DnsProviderInfo | null
}

export interface DnsDiagnosticRecord {
  key: 'spf' | 'dkim' | 'return_path' | 'dmarc'
  hostname: string
  type: 'TXT' | 'CNAME'
  expected: string
  required: boolean
  state: DnsDiagnosticState
  hint: DnsDiagnosticHint | null
  found: {
    authoritative: string[] | null
    public: string[] | null
  }
}

export interface DnsDiagnostic extends DnsProviderContext {
  authoritative_reachable: boolean
  expected_source: 'mailersend' | 'default'
  summary: DnsDiagnosticSummary
  records: DnsDiagnosticRecord[]
  checked_at: string
}

export type DnsConnectStatus =
  | 'pending'
  | 'consented'
  | 'applying'
  | 'applied'
  | 'needs_review'
  | 'refused'
  | 'failed'
  | 'rollback_failed'

export type DnsConnectError = 'mailersend_unavailable' | 'provider_error' | 'credential_check_failed' | 'unknown'

export type DnsConnectStartError = 'not_configured' | 'unsupported_provider' | 'provider_unavailable'

export interface DnsConnectReviewItem {
  key: 'spf' | 'dkim' | 'return_path' | 'dmarc'
  reason: 'spf_not_mergeable' | 'dmarc_existing' | 'record_conflict' | 'cname_in_use' | 'dmarc_lookup_failed'
  hostname: string
  current: string[]
  expected: string
  message: string | null
}

export interface DnsConnectState {
  available: boolean
  connection: {
    id: number
    provider: 'ovh'
    zone: string
    status: DnsConnectStatus
    review: DnsConnectReviewItem[]
    error: DnsConnectError | null
    created_at: string
    applied_at: string | null
  } | null
}

const isLoadingDomain = ref(false)

export default function useDomainConfig() {
  const { t } = useI18n()
  const notification = useNotification()

  const domains = ref<EmailDomain[]>([])
  const currentDomain = ref<EmailDomain | null>(null)
  const dnsRecords = ref<DnsRecordsResponse | null>(null)

  const listDomains = async (): Promise<EmailDomain[]> => {
    isLoadingDomain.value = true
    try {
      const { data } = await axios.get('/merchants/email-domains')
      domains.value = data.data
      return domains.value
    } catch {
      notification.create('error', {
        text: t('pages.domain.config.notifications.listError'),
        timeout: 4000
      })
      return []
    } finally {
      isLoadingDomain.value = false
    }
  }

  const createDomain = async (domain: string): Promise<EmailDomain | null> => {
    isLoadingDomain.value = true
    try {
      const { data } = await axios.post('/merchants/email-domains', { domain })
      currentDomain.value = data.data
      notification.create('success', {
        text: t('pages.domain.config.notifications.createSuccess'),
        timeout: 4000
      })
      return currentDomain.value
    } catch (error: any) {
      const message = error.response?.data?.errors?.domain?.[0] || t('pages.domain.config.notifications.createError')
      notification.create('error', {
        text: message,
        timeout: 4000
      })
      return null
    } finally {
      isLoadingDomain.value = false
    }
  }

  const getDnsRecords = async (domainId: number): Promise<DnsRecordsResponse | null> => {
    isLoadingDomain.value = true
    try {
      const { data } = await axios.get(`/merchants/email-domains/${domainId}/dns-records`)
      dnsRecords.value = data.data
      return dnsRecords.value
    } catch {
      notification.create('error', {
        text: t('pages.domain.config.notifications.dnsError'),
        timeout: 4000
      })
      return null
    } finally {
      isLoadingDomain.value = false
    }
  }

  const getDnsDiagnostic = async (domainId: number): Promise<DnsDiagnostic | null> => {
    try {
      const { data } = await axios.get(`/merchants/email-domains/${domainId}/dns-diagnostic`)
      return data.data
    } catch {
      return null
    }
  }

  const getDnsProvider = async (domainId: number): Promise<DnsProviderContext | null> => {
    try {
      const { data } = await axios.get(`/merchants/email-domains/${domainId}/dns-diagnostic`, {
        params: { provider_only: 1 }
      })
      return data.data
    } catch {
      return null
    }
  }

  const getDnsConnect = async (domainId: number): Promise<DnsConnectState | null> => {
    try {
      const { data } = await axios.get(`/merchants/email-domains/${domainId}/dns-connect`)
      return data.data
    } catch {
      return null
    }
  }

  const startDnsConnect = async (domainId: number): Promise<string | null> => {
    try {
      const { data } = await axios.post(`/merchants/email-domains/${domainId}/dns-connect`)
      return data.data.validation_url
    } catch (error: any) {
      const code: DnsConnectStartError | undefined = error.response?.data?.error
      notification.create('error', {
        text: t(
          code ? `pages.domain.config.dnsConnect.startErrors.${code}` : 'pages.domain.config.dnsConnect.startError'
        ),
        timeout: 8000
      })
      return null
    }
  }

  const deleteDomain = async (domainId: number): Promise<boolean> => {
    isLoadingDomain.value = true
    try {
      await axios.delete(`/merchants/email-domains/${domainId}`)
      domains.value = domains.value.filter((d) => d.id !== domainId)
      if (currentDomain.value?.id === domainId) currentDomain.value = null
      notification.create('success', {
        text: t('pages.domain.config.notifications.deleteSuccess'),
        timeout: 4000
      })
      return true
    } catch {
      notification.create('error', {
        text: t('pages.domain.config.notifications.deleteError'),
        timeout: 4000
      })
      return false
    } finally {
      isLoadingDomain.value = false
    }
  }

  const verifyDomain = async (domainId: number): Promise<EmailDomain | null> => {
    isLoadingDomain.value = true
    try {
      const { data } = await axios.post(`/merchants/email-domains/${domainId}/verify`)
      currentDomain.value = data.data
      return currentDomain.value
    } catch {
      notification.create('error', {
        text: t('pages.domain.config.notifications.verifyError'),
        timeout: 4000
      })
      return null
    } finally {
      isLoadingDomain.value = false
    }
  }

  const sendTestEmail = async (
    to: string
  ): Promise<{
    success: boolean
    error?: 'email_settings_not_configured' | 'sender_domain_not_verified' | 'unknown'
  }> => {
    try {
      await axios.post('/merchants/email-test', { to })
      notification.create('success', {
        text: t('pages.domain.testEmail.notifications.success'),
        timeout: 4000
      })
      return { success: true }
    } catch (error: any) {
      const errorCode = error.response?.data?.error

      if (errorCode === 'email_settings_not_configured') {
        notification.create('error', {
          text: t('pages.domain.testEmail.notifications.settingsNotConfigured'),
          timeout: 4000
        })
        return { success: false, error: 'email_settings_not_configured' }
      }

      if (errorCode === 'sender_domain_not_verified') {
        notification.create('error', {
          text: t('pages.domain.testEmail.notifications.domainNotVerified'),
          timeout: 4000
        })
        return { success: false, error: 'sender_domain_not_verified' }
      }

      notification.create('error', {
        text: t('pages.domain.testEmail.notifications.error'),
        timeout: 4000
      })
      return { success: false, error: 'unknown' }
    }
  }

  const sendDnsInstructions = async (domainId: number, to: string): Promise<boolean> => {
    try {
      await axios.post(`/merchants/email-domains/${domainId}/send-dns-instructions`, { to })
      notification.create('success', {
        text: t('pages.domain.config.steps.provider.delegate.successMessage'),
        timeout: 4000
      })
      return true
    } catch {
      notification.create('error', {
        text: t('pages.domain.config.steps.provider.delegate.errorMessage'),
        timeout: 4000
      })
      return false
    }
  }

  return {
    createDomain,
    currentDomain,
    deleteDomain,
    dnsRecords,
    domains,
    getDnsConnect,
    getDnsDiagnostic,
    getDnsProvider,
    getDnsRecords,
    isLoadingDomain,
    listDomains,
    sendDnsInstructions,
    sendTestEmail,
    startDnsConnect,
    verifyDomain
  }
}
